import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import MenuItem from "@mui/material/MenuItem";
import Avatar from "@mui/material/Avatar";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import format from "date-fns/format";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";

// Custom hooks
import useDepartmentMembersData from "layouts/tables/data/departmentMemberDataTable";

const money = (value) =>
  `GHS ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

function DepartmentMembers({ departmentId }) {
  const {
    columns,
    attendanceColumns,
    duesColumns,
    rows,
    attendanceRows,
    duesRows,
    duesSummary,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedMember,
    setSelectedMember,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddMember,
    showAttendanceView,
    showDuesView,
    toggleAttendanceView,
    toggleDuesView,
    showOnlyUnchecked,
    setShowOnlyUnchecked,
    dateRange,
    setDateRange,
    fetchDepartmentAttendance,
    filteredAttendanceRows,
    duesModalOpen,
    setDuesModalOpen,
    handleOpenDuesModal,
    duesForm,
    setDuesForm,
    duesSearchTerm,
    setDuesSearchTerm,
    duesFilteredMembers,
    isLoadingDuesMembers,
    showDuesMemberDropdown,
    setShowDuesMemberDropdown,
    handleDuesMemberSelect,
    handleRecordDuesSubmit,
    isSavingDues,
  } = useDepartmentMembersData(departmentId);

  const formatDisplayDate = (date) => {
    try {
      return format(date, "MMM d, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  const handleStartDateChange = (date) => {
    const newDateRange = { ...dateRange, startDate: date };
    setDateRange(newDateRange);
    fetchDepartmentAttendance(date, dateRange.endDate);
  };

  const handleEndDateChange = (date) => {
    const newDateRange = { ...dateRange, endDate: date };
    setDateRange(newDateRange);
    fetchDepartmentAttendance(dateRange.startDate, date);
  };

  const handleTodayClick = () => {
    const today = new Date();
    setDateRange({ startDate: today, endDate: today });
    fetchDepartmentAttendance(today, today);
  };

  if (isLoading) {
    return (
      <Box pt={6} pb={3}>
        <Typography>Loading department members...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={6} pb={3}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={
          showDuesView
            ? "Department Dues & Contributions"
            : `${
                showAttendanceView ? "Department Attendance" : "Department Members"
              } - ${formatDisplayDate(dateRange.startDate)}${
                dateRange.startDate.getTime() !== dateRange.endDate.getTime()
                  ? ` to ${formatDisplayDate(dateRange.endDate)}`
                  : ""
              }`
        }
        actions={
          <>
            {showAttendanceView && (
              <>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="From Date"
                    value={dateRange.startDate}
                    onChange={handleStartDateChange}
                    maxDate={dateRange.endDate}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ width: 150 }} />
                    )}
                  />
                  <DatePicker
                    label="To Date"
                    value={dateRange.endDate}
                    onChange={handleEndDateChange}
                    minDate={dateRange.startDate}
                    renderInput={(params) => (
                      <TextField {...params} size="small" sx={{ width: 150 }} />
                    )}
                  />
                </LocalizationProvider>
                <Button variant="outlined" onClick={handleTodayClick}>
                  Today
                </Button>
                <Button
                  variant={showOnlyUnchecked ? "contained" : "outlined"}
                  onClick={() => setShowOnlyUnchecked(!showOnlyUnchecked)}
                >
                  {showOnlyUnchecked ? "Show All" : "Show Only Unchecked"}
                </Button>
              </>
            )}
            {!showDuesView && (
              <Button
                variant={showAttendanceView ? "contained" : "outlined"}
                onClick={toggleAttendanceView}
              >
                {showAttendanceView ? "View Members" : "View Attendance"}
              </Button>
            )}
            {!showAttendanceView && (
              <Button variant={showDuesView ? "contained" : "outlined"} onClick={toggleDuesView}>
                {showDuesView ? "View Members" : "View Dues"}
              </Button>
            )}
            {showDuesView && (
              <Button
                variant="contained"
                color="success"
                startIcon={<Icon>volunteer_activism</Icon>}
                onClick={handleOpenDuesModal}
              >
                Record Dues
              </Button>
            )}
            {!showAttendanceView && !showDuesView && (
              <>
                <Button variant="contained" startIcon={<Icon>add</Icon>} onClick={handleAddMember}>
                  Add Member
                </Button>
                <Button variant="contained" onClick={handlePrint}>
                  <Icon>print</Icon>
                </Button>
              </>
            )}
          </>
        }
      />

      {showDuesView && (
        <Stack direction="row" spacing={1.5} mb={2} flexWrap="wrap">
          <Chip label={`Total: ${money(duesSummary.total)}`} color="success" />
          <Chip label={`${duesSummary.count} payment(s)`} variant="outlined" />
          {Object.entries(duesSummary.byType || {}).map(([type, amount]) => (
            <Chip key={type} label={`${type}: ${money(amount)}`} variant="outlined" />
          ))}
        </Stack>
      )}

      <Card>
        <Box pt={3}>
          <DataTable
            table={{
              columns: showDuesView
                ? duesColumns
                : showAttendanceView
                ? attendanceColumns
                : columns,
              rows: showDuesView ? duesRows : showAttendanceView ? filteredAttendanceRows : rows,
            }}
            isSorted={false}
            entriesPerPage={true}
            showTotalEntries={true}
            noEndBorder
            canSearch={true}
            searchLabel={
              showDuesView
                ? "Search dues..."
                : showAttendanceView
                ? "Search attendance..."
                : "Search members..."
            }
          />
        </Box>
      </Card>

      {/* Edit Member Modal */}
      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={selectedMember?.id ? "Edit Member" : "Add New Member"}
        onSave={handleEditSubmit}
        saveLabel={selectedMember?.id ? "Save Changes" : "Add Member"}
        maxWidth="xs"
      >
        <FormSection>
          <TextField
            label="Member Name"
            value={selectedMember?.name || ""}
            onChange={(e) =>
              setSelectedMember({
                ...selectedMember,
                name: e.target.value,
              })
            }
            fullWidth
            margin="normal"
            required
          />
        </FormSection>
      </FormDialog>

      {/* Record Dues / Contribution Modal */}
      <FormDialog
        open={duesModalOpen}
        onClose={() => setDuesModalOpen(false)}
        title="Record Dues / Contribution"
        onSave={handleRecordDuesSubmit}
        saveLabel={isSavingDues ? "Saving..." : "Record Payment"}
        saveDisabled={!duesForm.member_id || !duesForm.amount || isSavingDues}
        loading={isSavingDues}
        maxWidth="sm"
      >
        <FormSection>
          <FormControl fullWidth>
            <TextField
              label="Search Member"
              value={duesSearchTerm}
              onChange={(e) => {
                setDuesSearchTerm(e.target.value);
                setShowDuesMemberDropdown(e.target.value.trim() !== "");
              }}
              onFocus={() => {
                if (duesSearchTerm.trim() !== "" && duesFilteredMembers.length > 0) {
                  setShowDuesMemberDropdown(true);
                }
              }}
              onBlur={() => setTimeout(() => setShowDuesMemberDropdown(false), 200)}
              fullWidth
              margin="normal"
              required
              InputProps={{
                endAdornment: isLoadingDuesMembers && (
                  <CircularProgress color="inherit" size={20} />
                ),
              }}
            />
            {showDuesMemberDropdown && (
              <Box
                sx={{
                  position: "relative",
                  zIndex: 1300,
                  width: "100%",
                  maxHeight: 250,
                  overflowY: "auto",
                  bgcolor: "background.paper",
                  boxShadow: 3,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  mt: 0.5,
                }}
              >
                {duesFilteredMembers.length > 0 ? (
                  duesFilteredMembers.map((member) => (
                    <MenuItem
                      key={member.id}
                      onClick={() => handleDuesMemberSelect(member)}
                      sx={{ py: 1, px: 2, display: "flex", alignItems: "center", gap: 2 }}
                    >
                      <Avatar sx={{ width: 36, height: 36 }}>
                        <Icon>person</Icon>
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          {member.first_name} {member.surname}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {member.memberID}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled sx={{ py: 1, px: 2 }}>
                    {isLoadingDuesMembers ? "Loading..." : "No members found"}
                  </MenuItem>
                )}
              </Box>
            )}
          </FormControl>

          <TextField
            select
            label="Payment Type"
            value={duesForm.type}
            onChange={(e) => setDuesForm({ ...duesForm, type: e.target.value })}
            fullWidth
            margin="normal"
          >
            <MenuItem value="dues">Dues</MenuItem>
            <MenuItem value="contribution">Contribution</MenuItem>
            <MenuItem value="offering">Offering</MenuItem>
            <MenuItem value="tithe">Tithe</MenuItem>
            <MenuItem value="welfare">Welfare</MenuItem>
            <MenuItem value="harvest">Harvest</MenuItem>
          </TextField>

          <TextField
            label="Amount"
            type="number"
            value={duesForm.amount}
            onChange={(e) => setDuesForm({ ...duesForm, amount: e.target.value })}
            fullWidth
            margin="normal"
            required
            InputProps={{ startAdornment: <InputAdornment position="start">GHS</InputAdornment> }}
          />
        </FormSection>
      </FormDialog>

      {/* Delete Member Modal */}
      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedMember?.name}"?`}
        confirmLabel="Delete"
      />
    </Box>
  );
}

DepartmentMembers.defaultProps = {
  departmentId: null,
};

DepartmentMembers.propTypes = {
  // Optional — the parent route may render this component without an id
  // (e.g. /departments index page). The data hook handles a null id.
  departmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default DepartmentMembers;
